import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Plot from 'react-plotly.js';
import { ManRadioButtonGroup, ManRadioButton, ManText } from '@craft-design-system/core-components-react';
import useGraphRadio from '../../utils/useGraphRadio';
import Shimmer_graph from '../shimmer_graph';
import PopoverComponent from '../popover';
import { Card } from 'react-bootstrap';
import AverageDisplay from './Average_value';
import SegmentedControl from './SegmentedControl';
import useSegmentedChartQuery from '../../utils/useSegmentedChartQuery';
import Zoom from '../Zoom';
import DownloadButtonComponent from '../DownloadButtonComponent';
import { useFilterContext } from '../FilterContext';
import AIToggleButton from '../AIToggleButton';

const EnergyRecuperated = ({ value }) => {
  const [energy_recuperated, set_energy_recuperated] = useState(null);
  const radio_query = useGraphRadio();
  const [radio_buttons_test, set_Radio_values] = useState(null);
  const { filterdata } = useFilterContext();
  const { startDate, endDate } = filterdata || {};
  const [isLoading, setIsLoading] = useState(false);
  const [avg_value, setavg_value] = useState(value.RecuperatedEnergy[2]['average']);
  const [downloadData, setDownloadData] = useState(JSON.parse(value.RecuperatedEnergy[3]));
  const [chartType, setChartType] = useState("Bar");
  const { fetchSegmentedChart } = useSegmentedChartQuery();
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Handle segmented chart fetch
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const data = await fetchSegmentedChart({
          vinShort: value.vin_short,
          chartType,
          columnname: "Energy Recuperated",
        });

        console.log("📊 Segmented chart response:", data);
        
        if (chartType === "Bar") {
          set_energy_recuperated(JSON.parse(data.graph_data[0]));
          setDownloadData(JSON.parse(data.graph_data[3]));
        } else {
          set_energy_recuperated(JSON.parse(data[0]));
          setDownloadData(JSON.parse(data[1]));
        }
      } catch (error) {
        console.error("❌ Error fetching chart data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (!isInitialLoad && chartType && value?.vin_short) {
      fetchData();
    } else {
      setIsInitialLoad(false);
    }
  }, [chartType]);


  // Handle radio button fetch
  useEffect(() => {
    if (radio_buttons_test) {
      console.log("🔄 Radio button changed:", radio_buttons_test);

      setIsLoading(true);

      const requestParams = {
        vins_short: [value.vin_short],
        radio_value: radio_buttons_test,
        columnname: "Energy Recuperated",
        startDate,
        endDate,
        filterdata: filterdata,
        resetdata: filterdata?.resetdata
      };

      console.log("📡 Fetching graph data with:", requestParams);

      radio_query(requestParams)
        .then(data => {
          console.log("✅ Response from radio_query:", data);

          try {
            const graphData = JSON.parse(data.jsonData.graph_data[0]);
            const avgVal = data.jsonData.graph_data[2].average;

            set_energy_recuperated(graphData);
            setavg_value(avgVal);
            
            console.log("📊 Graph updated:", graphData);
            console.log("📈 Average value:", avgVal);
          } catch (err) {
            console.error("❌ Error parsing graph_data:", err);
          }
        })
        .catch(error => {
          console.error("❌ Error in radio_query:", error);
        })
        .finally(() => {
          console.log("⏳ Finished radio_query fetch cycle");
          setIsLoading(false);
        });
    }
  }, [radio_buttons_test]);

  // Initial fallback data
  useEffect(() => {
    try {
      set_energy_recuperated(JSON.parse(value.RecuperatedEnergy[0]));
      setDownloadData(JSON.parse(value.RecuperatedEnergy[3]));
    } catch (error) {
      console.error("Error parsing fallback energy_recuperated JSON", error);
    }
  }, [value.RecuperatedEnergy]);



  return (
    <Card className="card_ride">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <ManText className="card-title-text" variant="component-l" as="span" size="l">
            Recuperated energy
          </ManText>
        </div>
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', flexDirection: "row-reverse" }}>
          <PopoverComponent
            id="Energy_Recuperated"
            content="<b>Energy Recuperated:</b> The amount of energy recuperated during vehicle usage.<br/><br/><b>Telematic Raw Data:</b><br/>• TBM3: payloaddecoded_recuperatedenergy_value<br/>• CM4: payload_electricmessagesystem_recuperatedenergy (BBP), payload_electricmessagesystem_batrecenergycbp (CBP)<br/><br/><b>Time Series Data:</b><br/>If multiple vehicles are selected average values are displayed. Days without data are not taken into account for the calculation.<br/><br/><b>Histogram:</b><br/>Each bar represents the number of days that fall within a certain range (bin) of <b>recuperated energy values</b>. All daily values since start of data acquisition for the selected vehicle(s) are visualized per default.<br/><br/><b>Boxplot:</b><br/>The boxplot shows the <b>distribution of daily recuperated energy values</b> for the selected vehicle(s) since start of data acquisition. If multiple vehicles are selected, the boxplot reflects the <b>combined distribution</b> of all their daily values.<br/>• The <b>box</b> represents the <b>middle 50%</b> of values (from the 25th percentile ('q1') to the 75th percentile ('q3'))<br/>• The <b>line inside the box</b> shows the <b>median</b> (50th percentile)<br/>• The whiskers extend to show the range of typical values ('min' and 'max') and visualize <b>the spread of the data</b> beyond the central box."
          />
          <Zoom chartData={energy_recuperated?.data} chartLayout={energy_recuperated?.layout} chartTitle="Energy Recuperated" />
          <DownloadButtonComponent data={downloadData} fileName='energy_recuperated_data.csv' />
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px', marginTop: '16px' }}>
        {chartType === 'Bar' ? <AverageDisplay averages={avg_value} unit="kWh/100km" /> : <div style={{ width: '150px' }} />}
        {chartType === 'Bar' ? (
          <ManRadioButtonGroup
            name="Energy Recuperated"
            onManChange={(e) => set_Radio_values({ [e.detail.name]: e.detail.value })}
            style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}
          >
            <ManRadioButton name="Energy Recuperated" label="Daily" value="Daily" checked />
            <ManRadioButton name="Energy Recuperated" label="Monthly" value="Monthly" />
            <ManRadioButton name="Energy Recuperated" label="Yearly" value="Yearly" />
          </ManRadioButtonGroup>
        ) : <div style={{ flexGrow: 1 }} />}
        <SegmentedControl selected={chartType} onChange={setChartType} />
      </div>

      {isLoading ? (
        <Shimmer_graph />
      ) : (
        energy_recuperated && (
          <>
            <Plot
              data={energy_recuperated.data}
              layout={{
                ...energy_recuperated.layout,
                autosize: true,
                responsive: true,
                margin: chartType === 'Bar' 
                  ? { t: 10, r: 10, l: 10, b: 10 }
                  : { t: 10, r: 10, l: 10, b: 160 }
              }}
              useResizeHandler
            />

            {/* 🔥 AI Toggle Button - Only show for Bar charts with data */}
            {chartType === 'Bar' && downloadData?.length > 0 && (
              <div style={{
                display: 'flex',
                justifyContent: 'flex-start',
                paddingLeft: '10px',
                marginTop: '8px',
                marginBottom: '8px'
              }}>
                <AIToggleButton
                  downloadData={downloadData}
                  radioButtonState={radio_buttons_test || { 'Energy Recuperated': 'Daily' }}
                  kpiName="Energy Recuperation"
                  chartType={chartType}
                  vinShort={value.vin_short}
                  title="AI | Automotive Intelligence - Energy Recuperation Analysis"
                />
              </div>
            )}
          </>
        )
      )}
    </Card>
  );
};

EnergyRecuperated.propTypes = {
  value: PropTypes.shape({
    vin_short: PropTypes.string.isRequired,
    RecuperatedEnergy: PropTypes.array.isRequired,
  }).isRequired,
};

export default EnergyRecuperated;
